import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useLocation, useNavigate } from 'react-router-dom';
import { Modal, Button } from '../common';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '';

function AnswerModal({ problemId, solution, onClose, onUpdate }) {
  const { getAccessToken } = useAuth();
  const navigate = useNavigate();
  const [multipleChoice, setMultipleChoice] = useState('');
  const [subjectiveAnswer, setSubjectiveAnswer] = useState('');
  const [lastActiveType, setLastActiveType] = useState('subjective'); // 마지막으로 활성화된 타입
  const [isLoading, setIsLoading] = useState(false);
  const location = useLocation();

  // 기존 답안 설정
  useEffect(() => {
    if (solution?.solution_info?.answer) {
      const answer = solution.solution_info.answer;
      const multipleChoices = ['①', '②', '③', '④', '⑤'];
      if (multipleChoices.includes(answer)) {
        setMultipleChoice(answer);
        setLastActiveType('multiple');
      } else {
        setSubjectiveAnswer(answer);
        setLastActiveType('subjective');
      }
    }
  }, [solution]);

  // 객관식 선택 시
  const handleMultipleChoiceChange = (choice) => {
    setMultipleChoice(choice);
    setLastActiveType('multiple');
  };

  // 주관식 입력 시
  const handleSubjectiveChange = (value) => {
    setSubjectiveAnswer(value);
    if (value.trim()) {
      setLastActiveType('subjective');
    }
  };

  // 주관식 포커스 시
  const handleSubjectiveFocus = () => {
    setLastActiveType('subjective');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    // 마지막으로 활성화된 타입에 따라 답안 결정
    const finalAnswer = lastActiveType === 'multiple' ? multipleChoice : subjectiveAnswer;
    
    if (!finalAnswer.trim()) {
      alert('답안을 입력해주세요.');
      setIsLoading(false);
      return;
    }

    console.log('Submit data:', { 
      problemId, 
      solution, 
      finalAnswer,
      lastActiveType 
    });

    try {
      const token = await getAccessToken();
      if (!token) throw new Error('인증 토큰이 없습니다.');

      const url = solution 
        ? `/api/solutions/${solution.solution_id}/answer`
        : '/api/solutions';

      const method = solution ? 'PUT' : 'POST';
      const body = solution 
        ? { answer: finalAnswer }
        : { problem_id: Number(problemId), answer: finalAnswer };  // problemId를 숫자로 변환
      
      console.log('API Request:', { 
        url, 
        method, 
        body,
        problemIdType: typeof problemId,
        problemIdValue: problemId 
      });

      const response = await fetch(`${API_BASE_URL}${url}`, {
        method,
        headers: {
          'Authorization': token,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
        credentials: 'include',
      });

      const data = await response.json();
      
      // API 응답 상태 확인
      if (!response.ok || data.status !== 'success') {
        console.error('API Error:', data);
        throw new Error(data.message || 'API 요청 실패');
      }

      alert(solution ? '정답이 수정되었습니다.' : '정답이 등록되었습니다.');
      
      // onUpdate 콜백이 있으면 사용하고, 없으면 현재 페이지를 새로고침
      if (onUpdate) {
        onUpdate();
        onClose();  // onUpdate 후에 onClose 호출
      } else {
        onClose();  // onUpdate가 없으면 바로 onClose 호출
        // sessionStorage에서 이전 URL 가져오기
        const previousUrl = sessionStorage.getItem('previousUrl');
        if (previousUrl && previousUrl.includes('labels=')) {
          // 이전 페이지가 라벨 필터였던 경우 해당 URL로 이동
          navigate(previousUrl.replace(window.location.origin, ''));
        } else {
          // 현재 페이지 새로고침 대신 navigate를 사용하여 상태 갱신
          navigate(location.pathname + location.search, { replace: true });
        }
      }
    } catch (error) {
      console.error('정답 처리 중 오류:', error);
      alert(`정답 처리에 실패했습니다: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal
      isOpen={true}
      onClose={onClose}
      title={solution ? '정답 수정' : '정답 등록'}
      size="md"
      zIndex={9999890}
      footer={
        <div className="flex w-full justify-end gap-2">
          <Button
            type="button"
            variant="secondary"
            onClick={onClose}
          >
            취소
          </Button>
          <Button
            type="submit"
            variant="primary"
            form="answer-form"
            loading={isLoading}
          >
            {isLoading ? '처리 중...' : (solution ? '수정' : '등록')}
          </Button>
        </div>
      }
    >
      <form id="answer-form" onSubmit={handleSubmit}>
        {/* 활성화 상태 표시 */}
        <div className="mb-4 rounded-3xl bg-gray-50 p-3">
          <p className="text-sm text-gray-700">
            <span className="font-medium">저장될 답안:</span>{' '}
            {lastActiveType === 'multiple' ? (
              multipleChoice ? <span className="font-semibold text-blue-600">{multipleChoice} (객관식)</span> : <span className="text-gray-400">객관식을 선택하세요</span>
            ) : (
              subjectiveAnswer ? <span className="font-semibold text-green-600">"{subjectiveAnswer}" (주관식)</span> : <span className="text-gray-400">주관식을 입력하세요</span>
            )}
          </p>
        </div>

        {/* 객관식 선택 */}
        <div className={`mb-4 rounded-3xl border-2 p-3 transition-colors ${
          lastActiveType === 'multiple' ? 'border-blue-200 bg-blue-50' : 'border-gray-200'
        }`}>
          <label className="mb-2 block text-sm font-medium text-gray-700">
            객관식 정답 선택
            {lastActiveType === 'multiple' && <span className="ml-2 text-xs font-semibold text-blue-600">● 활성화</span>}
          </label>
          <div className="flex gap-2">
            {['①', '②', '③', '④', '⑤'].map((choice) => (
              <label 
                key={choice} 
                className="flex cursor-pointer items-center rounded p-1 hover:bg-blue-100"
              >
                <input
                  type="radio"
                  name="multipleChoice"
                  value={choice}
                  checked={multipleChoice === choice}
                  onChange={(e) => handleMultipleChoiceChange(e.target.value)}
                  className="mr-1"
                />
                <span className="text-lg">{choice}</span>
              </label>
            ))}
          </div>
        </div>

        {/* 주관식 입력 */}
        <div className={`mb-4 rounded-3xl border-2 p-3 transition-colors ${
          lastActiveType === 'subjective' ? 'border-green-200 bg-green-50' : 'border-gray-200'
        }`}>
          <label className="mb-2 block text-sm font-medium text-gray-700">
            주관식 정답 입력
            {lastActiveType === 'subjective' && <span className="ml-2 text-xs font-semibold text-green-600">● 활성화</span>}
          </label>
          <input
            type="text"
            value={subjectiveAnswer}
            onChange={(e) => handleSubjectiveChange(e.target.value)}
            onFocus={handleSubjectiveFocus}
            className="w-full rounded-3xl border-gray-300 shadow-sm focus:border-primary focus:ring-primary"
            placeholder="정답을 입력하세요"
          />
        </div>
      </form>
    </Modal>
  );
}

export default AnswerModal; 